/* eslint-disable no-underscore-dangle */
import * as t from "io-ts"
import jwtDecode from "jwt-decode"
import { User } from "../types"

export const USER_CLAIM = `https://platform.flock.com/user` as const

// --------------------------------------------------------------------------------------------------------------------

const UserClaimType = t.union([
  t.type({ kind: t.literal(`customer`), id: t.string, ownerId: t.string }),
  t.type({ kind: t.literal(`broker`), id: t.string, ownerId: t.string }),
  t.type({ kind: t.literal(`claims_admin`), id: t.string, ownerId: t.string }),
  t.type({ kind: t.literal(`carrier`), id: t.string, ownerId: t.string }),
  t.type({ kind: t.literal(`flocker`), id: t.string }),
])

const PayloadType = t.intersection([
  t.partial({
    permissions: t.readonlyArray(t.string),
  }),
  t.type({
    [USER_CLAIM]: UserClaimType,
  }),
])

// --------------------------------------------------------------------------------------------------------------------

export type UserClaim = t.TypeOf<typeof UserClaimType>
export type Payload = t.TypeOf<typeof PayloadType>

// --------------------------------------------------------------------------------------------------------------------

const unwrapPayload = (token: string): Payload => {
  const validated = PayloadType.decode(jwtDecode(token))

  if (validated._tag === `Left`) throw new Error(`Error unwrapping auth`)

  return validated.right
}

// --------------------------------------------------------------------------------------------------------------------

export const decode = (token: string): User => {
  const payload = unwrapPayload(token)
  const user = payload[USER_CLAIM]

  switch (user.kind) {
    case `flocker`:
      return {
        id: user.id,
        permissions: payload.permissions ?? [],
        kind: { tag: `flocker` },
      }

    case `broker`:
      return {
        id: user.id,
        permissions: payload.permissions ?? [],
        kind: { tag: `broker`, brokerId: user.ownerId },
      }

    case `customer`:
      return {
        id: user.id,
        permissions: payload.permissions ?? [],
        kind: { tag: `customer`, customerId: user.ownerId },
      }

    case `claims_admin`:
      return {
        id: user.id,
        permissions: payload.permissions ?? [],
        kind: { tag: `claims_admin`, claimsAdminId: user.ownerId },
      }

    case `carrier`:
      return {
        id: user.id,
        permissions: payload.permissions ?? [],
        kind: { tag: `carrier`, carrierId: user.ownerId },
      }

    default:
      throw new Error(`Unknown user kind`)
  }
}

export const useAuthDecode = () => decode
